import type { Country } from "@/lib/i18n";

/**
 * CAD pricing helpers. USD amounts are the source of truth in plan data;
 * Canadian pages display CAD via a configurable multiplier.
 */

const DEFAULT_USD_TO_CAD = 1.35;

export function getUsdToCadRate(): number {
  const raw = process.env.NEXT_PUBLIC_USD_TO_CAD_RATE?.trim();
  if (!raw) return DEFAULT_USD_TO_CAD;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_USD_TO_CAD;
  return n;
}

export function parseUsdAmount(priceStr: string): number {
  const n = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export function usdToCad(usd: number, rate = getUsdToCadRate()): number {
  return Math.round(usd * rate);
}

/**
 * Format a USD price string for the visitor's country.
 * US: "$49" · CA: "CA$66"
 */
export function formatPrice(
  usdPriceStr: string,
  options: { country: Country; yearly?: boolean } = { country: "US" }
): string {
  const monthly = parseUsdAmount(usdPriceStr);
  const amountUsd = options.yearly ? Math.round(monthly * 0.8) : monthly;
  if (options.country === "CA") {
    return `CA$${usdToCad(amountUsd)}`;
  }
  return `$${amountUsd}`;
}

export function priceCurrency(country: Country): "USD" | "CAD" {
  return country === "CA" ? "CAD" : "USD";
}

/** Numeric amount for JSON-LD Offer.price (already converted when CA). */
export function offerPriceAmount(
  usdPriceStr: string,
  country: Country
): string {
  const usd = parseUsdAmount(usdPriceStr);
  if (country === "CA") return String(usdToCad(usd));
  return String(usd);
}
